import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SeoHead } from "@/components/SeoHead";
import { useAuth } from "@/hooks/useAuth";
import { BetaBanner } from "@/components/BetaBanner";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { getStripe } from "@/lib/stripe";
import { parsePlanParam } from "@/lib/signupPricing";
import { SignInForm } from "./auth/SignInForm";
import { SignUpForm } from "./auth/SignUpForm";
import { oauthReturnPath, rememberPendingOAuthReturn } from "@/lib/oauthReturnPath";

const Auth = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const validatedReturn = oauthReturnPath(searchParams.get("returnTo") ?? searchParams.get("next"), "");
  const returnTo = validatedReturn && !validatedReturn.startsWith("/auth") ? validatedReturn : null;
  const invitedEmail = searchParams.get("email");
  const initialTab = searchParams.get("tab") === "signup" ? "signup" : "signin";
  const planParam = parsePlanParam(searchParams.get("plan"));
  const { user } = useAuth();

  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);

  // Keep a Claude.ai consent URL alive through signup (checkout + magic link).
  useEffect(() => {
    if (returnTo) rememberPendingOAuthReturn(returnTo);
  }, [returnTo]);

  useEffect(() => {
    if (!user) return;
    navigate(returnTo ?? "/dashboard", { replace: true });
  }, [user, navigate, returnTo]);

  return (
    <div className="min-h-dvh flex items-center justify-center p-4 bg-background">
      <SeoHead
        title="Sign in or create an account — Excerptus"
        description="Sign in to your Excerptus workspace or start a 10-day free trial on any plan."
      />
      <div className="w-full max-w-2xl space-y-6">
        <div className="text-center">
          <h1 className="font-serif text-4xl font-semibold text-primary">Excerptus</h1>
          <p className="text-muted-foreground mt-2">The Heart of a Knowledge Worker's Second Brain.</p>
          <div className="mt-4 flex justify-center"><BetaBanner /></div>
        </div>

        {clientSecret ? (
          <Card className="shadow-elevated">
            <CardHeader>
              <h2 className="font-serif text-xl font-semibold">Add a card to start your trial</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Nothing is charged until day 11. Cancel anytime during the trial.
              </p>
            </CardHeader>
            <CardContent>
              <EmbeddedCheckoutProvider stripe={getStripe()} options={{ clientSecret }}>
                <EmbeddedCheckout />
              </EmbeddedCheckoutProvider>
            </CardContent>
          </Card>
        ) : alreadyRegistered ? (
          <Card className="shadow-elevated">
            <CardHeader><h2 className="font-serif text-xl font-semibold">Check your email</h2></CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                If an account exists for that email, we've sent you a sign-in link.
              </p>
              <Button variant="outline" onClick={() => setAlreadyRegistered(false)}>Back</Button>
            </CardContent>
          </Card>
        ) : (
          <Card className="shadow-elevated">
            <Tabs defaultValue={initialTab}>
              <CardHeader>
                <TabsList className="grid grid-cols-2 w-full">
                  <TabsTrigger value="signin">Sign in</TabsTrigger>
                  <TabsTrigger value="signup">Create account</TabsTrigger>
                </TabsList>
              </CardHeader>
              <CardContent className="space-y-4">
                <TabsContent value="signin">
                  <SignInForm initialEmail={invitedEmail ?? ""} returnTo={returnTo} />
                </TabsContent>
                <TabsContent value="signup">
                  <SignUpForm
                    initialEmail={invitedEmail ?? ""}
                    initialTier={planParam?.tier ?? "starter"}
                    initialBilling={planParam?.billing ?? "monthly"}
                    onCheckoutReady={setClientSecret}
                    onAlreadyRegistered={() => setAlreadyRegistered(true)}
                  />
                </TabsContent>
              </CardContent>
            </Tabs>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Auth;
